import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { adminApi } from '../../utils/api';
import {
    Button, Badge, Card, Modal, Alert, Spinner, Tabs, InfoRow, Pagination,
} from './shared';

const toISODate = (d) => d.toISOString().split('T')[0];

const PERIOD_PRESETS = [
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: '7', label: '7 days' },
    { id: '14', label: '14 days' },
    { id: '30', label: '30 days' },
    { id: '90', label: '90 days' },
    { id: 'custom', label: 'Custom' },
];

const getDateRange = (presetId, customStart, customEnd) => {
    const today = new Date();
    if (presetId === 'today') {
        const d = toISODate(today);
        return { start_date: d, end_date: d };
    }
    if (presetId === 'yesterday') {
        const y = new Date(today);
        y.setDate(y.getDate() - 1);
        const d = toISODate(y);
        return { start_date: d, end_date: d };
    }
    if (presetId === 'custom' && customStart && customEnd) {
        return { start_date: customStart, end_date: customEnd };
    }
    const days = parseInt(presetId);
    const start = new Date(today);
    start.setDate(start.getDate() - days);
    return { start_date: toISODate(start), end_date: toISODate(today) };
};

const fmt = (n, decimals = 2) => {
    if (n == null) return '0';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return typeof n === 'number' && !Number.isInteger(n) ? n.toFixed(decimals) : String(n);
};

const fmtCost = (n) => `$${(n || 0).toFixed(4)}`;

const fmtDateTime = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return dt.toLocaleString('en-GB', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
};

function PeriodSelector({ preset, onPresetChange, customStart, customEnd, onCustomChange }) {
    return (
        <div className="flex items-center gap-1.5 flex-wrap">
            {PERIOD_PRESETS.map((p) => (
                <button
                    key={p.id}
                    type="button"
                    onClick={() => onPresetChange(p.id)}
                    className={`h-7 px-2.5 text-xs rounded-md border transition-colors ${
                        preset === p.id
                            ? 'bg-gray-900 text-white border-gray-900'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:text-gray-900'
                    }`}
                >
                    {p.label}
                </button>
            ))}
            {preset === 'custom' && (
                <div className="flex items-center gap-1.5 ml-1">
                    <input
                        type="date"
                        value={customStart}
                        onChange={(e) => onCustomChange(e.target.value, customEnd)}
                        className="h-7 px-2 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                    />
                    <span className="text-xs text-gray-400">-</span>
                    <input
                        type="date"
                        value={customEnd}
                        onChange={(e) => onCustomChange(customStart, e.target.value)}
                        className="h-7 px-2 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                    />
                </div>
            )}
        </div>
    );
}

function StatCard({ label, value, sub }) {
    return (
        <div className="rounded-lg border border-gray-200 bg-white px-5 py-4">
            <p className="text-sm text-gray-500">{label}</p>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
    );
}

function CostBar({ value, max }) {
    const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
    return (
        <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-mono text-gray-700 w-20 text-right shrink-0">{fmtCost(value)}</span>
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gray-900 rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}


const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const AREA_COLORS = [
    '#111827', '#6366f1', '#f59e0b', '#10b981', '#ef4444',
    '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#64748b',
];

function DropdownSelect({ value, onChange, options, className }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const current = options.find((o) => o.value === value);
    return (
        <div ref={ref} className={`relative ${className || ''}`}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="h-7 px-2.5 text-xs rounded-md border border-gray-200 bg-white text-gray-700 hover:border-gray-300 flex items-center gap-1"
            >
                {current?.label || value}
                <svg className={`w-3 h-3 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[120px]">
                    {options.map((o) => (
                        <button
                            key={o.value}
                            type="button"
                            onClick={() => { onChange(o.value); setOpen(false); }}
                            className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${o.value === value ? 'font-medium text-gray-900' : 'text-gray-600'}`}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function DailyUsageChart({ dateRange }) {
    const chartRef = useRef(null);
    const [groupBy, setGroupBy] = useState('model');
    const [metric, setMetric] = useState('spend');
    const [chartType, setChartType] = useState('area');
    const [breakdown, setBreakdown] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        adminApi.getTokenUsageDailyBreakdown({ ...dateRange, group_by: groupBy })
            .then(setBreakdown)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [dateRange, groupBy]);

    if (loading) return (
        <div className="border border-gray-200 rounded-lg bg-white p-6">
            <Spinner text="Loading chart..." center />
        </div>
    );
    if (!breakdown || breakdown.dates.length === 0) return null;

    const categories = breakdown.dates.map((d) => {
        const parts = d.split('-');
        return `${parts[1]}-${parts[2]}`;
    });
    const todayStr = toISODate(new Date());
    const todayIdx = breakdown.dates.indexOf(todayStr);

    const isCost = metric === 'spend';
    const yLabel = isCost ? 'Spend' : 'Tokens';
    const yFormat = isCost ? '${value}' : '{value}';

    const series = breakdown.series.map((s, i) => ({
        name: s.name,
        data: isCost ? s.cost : s.tokens,
        color: AREA_COLORS[i % AREA_COLORS.length],
        ...(chartType === 'area' ? { type: 'area', fillOpacity: 0.08, lineWidth: 2 } : { type: 'column' }),
    }));

    const options = {
        chart: {
            height: 260,
            style: { fontFamily: 'inherit' },
            spacingTop: 10,
        },
        title: { text: null },
        credits: { enabled: false },
        xAxis: {
            categories,
            crosshair: { width: 1, color: '#d1d5db', dashStyle: 'Dash' },
            labels: { style: { fontSize: '10px', color: '#9ca3af' }, rotation: breakdown.dates.length > 20 ? -45 : 0 },
            lineColor: '#e5e7eb',
            tickLength: 0,
            plotLines: todayIdx >= 0 ? [{
                value: todayIdx,
                color: '#9ca3af',
                dashStyle: 'Dash',
                width: 1,
                label: { text: 'Today', rotation: 0, style: { fontSize: '10px', color: '#6b7280' }, align: 'left', x: 4, y: 12 },
                zIndex: 5,
            }] : [],
        },
        yAxis: {
            title: { text: yLabel, style: { fontSize: '10px', color: '#9ca3af', fontWeight: '400' } },
            labels: { style: { fontSize: '10px', color: '#9ca3af' }, format: yFormat },
            gridLineColor: '#f3f4f6',
            min: 0,
        },
        tooltip: {
            shared: true,
            style: { fontSize: '11px' },
            valuePrefix: isCost ? '$' : '',
            valueSuffix: isCost ? '' : ' tokens',
            valueDecimals: isCost ? 4 : 0,
        },
        plotOptions: {
            area: { marker: { enabled: false, symbol: 'circle', radius: 3, states: { hover: { enabled: true } } } },
            column: { borderRadius: 2, borderWidth: 0, groupPadding: 0.15 },
            series: { stacking: chartType === 'area' ? undefined : 'normal' },
        },
        legend: {
            align: 'left',
            verticalAlign: 'bottom',
            symbolRadius: 2,
            symbolHeight: 8,
            symbolWidth: 12,
            itemStyle: { fontSize: '10px', color: '#6b7280', fontWeight: '400' },
        },
        series,
    };

    return (
        <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <div>
                    <h3 className="text-sm font-medium text-gray-900">Daily Usage</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Usage per day across your billing period</p>
                </div>
                <div className="flex items-center gap-1.5">
                    <DropdownSelect
                        value={groupBy}
                        onChange={setGroupBy}
                        options={[
                            { value: 'model', label: 'By Model' },
                            { value: 'agent', label: 'By Agent' },
                        ]}
                    />
                    <DropdownSelect
                        value={metric}
                        onChange={setMetric}
                        options={[
                            { value: 'spend', label: 'Spend' },
                            { value: 'tokens', label: 'Tokens' },
                        ]}
                    />
                    <DropdownSelect
                        value={chartType}
                        onChange={setChartType}
                        options={[
                            { value: 'area', label: 'Area' },
                            { value: 'column', label: 'Bar' },
                        ]}
                    />
                </div>
            </div>
            <div className="px-2">
                <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
            </div>
        </div>
    );
}

function HourlyHeatmapChart({ heatmap, peakHour, peakDay }) {
    if (!heatmap || heatmap.length === 0) return null;
    const maxReqs = Math.max(...heatmap.map(h => h.requests), 1);
    const grid = {};
    heatmap.forEach((h) => { grid[`${h.day}-${h.hour}`] = h; });

    return (
        <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">Activity Heatmap</h3>
                {peakHour != null && peakDay != null && (
                    <span className="text-xs text-gray-400">Peak: {DAY_NAMES[peakDay]} {peakHour}:00</span>
                )}
            </div>
            <div className="px-4 py-3 overflow-x-auto">
                <table className="w-full border-collapse">
                    <thead>
                        <tr>
                            <th className="text-[10px] text-gray-400 font-normal w-8" />
                            {Array.from({ length: 24 }, (_, i) => (
                                <th key={i} className="text-[10px] text-gray-400 font-normal px-0 text-center w-6">{i}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {[1, 2, 3, 4, 5, 6, 0].map((day) => (
                            <tr key={day}>
                                <td className="text-[10px] text-gray-400 pr-1">{DAY_NAMES[day]}</td>
                                {Array.from({ length: 24 }, (_, hour) => {
                                    const cell = grid[`${day}-${hour}`];
                                    const intensity = cell ? cell.requests / maxReqs : 0;
                                    const bg = intensity === 0 ? 'bg-gray-50'
                                        : intensity < 0.25 ? 'bg-gray-200'
                                        : intensity < 0.5 ? 'bg-gray-400'
                                        : intensity < 0.75 ? 'bg-gray-600'
                                        : 'bg-gray-900';
                                    return (
                                        <td key={hour} className="p-0.5">
                                            <div
                                                className={`w-full h-4 rounded-sm ${bg} group relative cursor-default`}
                                                title={cell ? `${DAY_NAMES[day]} ${hour}:00 — ${cell.requests} reqs, ${fmtCost(cell.cost)}` : `${DAY_NAMES[day]} ${hour}:00 — 0`}
                                            />
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="flex items-center gap-1 mt-2 justify-end">
                    <span className="text-[10px] text-gray-400">Less</span>
                    {['bg-gray-50', 'bg-gray-200', 'bg-gray-400', 'bg-gray-600', 'bg-gray-900'].map((c) => (
                        <div key={c} className={`w-3 h-3 rounded-sm ${c}`} />
                    ))}
                    <span className="text-[10px] text-gray-400">More</span>
                </div>
            </div>
        </div>
    );
}

function OverviewTab({ dateRange }) {
    const [data, setData] = useState(null);
    const [health, setHealth] = useState(null);
    const [users, setUsers] = useState([]);
    const [models, setModels] = useState([]);
    const [heatmap, setHeatmap] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [overview, h, usersRes, modelsRes, heatmapRes] = await Promise.all([
                adminApi.getTokenUsageOverview(dateRange),
                adminApi.getTrackingHealth(),
                adminApi.getTokenUsageByUser({ ...dateRange, limit: 10, offset: 0 }),
                adminApi.getTokenUsageByModel(dateRange),
                adminApi.getTokenUsageHourlyHeatmap(dateRange),
            ]);
            setData(overview);
            setHealth(h);
            setUsers(usersRes.users || []);
            setModels(modelsRes.models || []);
            setHeatmap(heatmapRes);
        } catch {}
        setLoading(false);
    }, [dateRange]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Spinner size="lg" text="Loading overview..." center />;
    if (!data) return <p className="text-sm text-gray-400 text-center py-8">No data</p>;

    const healthVariant = health?.status === 'healthy' ? 'success' : health?.status === 'idle' ? 'warning' : 'default';
    const maxUserCost = Math.max(...users.map(u => u.total_cost), 0.0001);
    const maxModelCost = Math.max(...models.map(m => m.total_cost), 0.0001);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-end">
                {health && <Badge variant={healthVariant}>Tracking: {health.status}</Badge>}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Total Requests" value={fmt(data.total_requests, 0)} />
                <StatCard label="Total Tokens" value={fmt(data.total_tokens, 0)} />
                <StatCard label="Total Cost" value={fmtCost(data.total_cost)} />
                <StatCard label="Active Users" value={data.active_users} />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <StatCard label="Avg Cost / Request" value={fmtCost(data.avg_cost_per_request)} />
                <StatCard label="Avg Tokens / Request" value={fmt(data.avg_tokens_per_request, 0)} />
            </div>

            <DailyUsageChart dateRange={dateRange} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                        <h3 className="text-sm font-medium text-gray-900">Top Users</h3>
                        <span className="text-xs text-gray-400">{users.length} users</span>
                    </div>
                    {users.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-6">No users in this period</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">User</th>
                                    <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Reqs</th>
                                    <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Tokens</th>
                                    <th className="px-4 py-2 text-xs font-medium text-gray-500 w-32">Cost</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {users.map((u) => (
                                    <tr key={u.user_id} className="hover:bg-gray-50">
                                        <td className="px-4 py-1.5 font-mono text-xs truncate max-w-[160px]">
                                            <Link
                                                to={`/admin/users/${encodeURIComponent(u.user_id)}`}
                                                className="text-blue-600 hover:text-blue-800 hover:underline"
                                            >
                                                {u.user_id}
                                            </Link>
                                        </td>
                                        <td className="px-4 py-1.5 text-right text-xs">{fmt(u.total_requests, 0)}</td>
                                        <td className="px-4 py-1.5 text-right text-xs">{fmt(u.total_tokens, 0)}</td>
                                        <td className="px-4 py-1.5"><CostBar value={u.total_cost} max={maxUserCost} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                        <h3 className="text-sm font-medium text-gray-900">Models Used</h3>
                        <span className="text-xs text-gray-400">{models.length} models</span>
                    </div>
                    {models.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-6">No models in this period</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Model</th>
                                    <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Reqs</th>
                                    <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Tokens</th>
                                    <th className="px-4 py-2 text-xs font-medium text-gray-500 w-32">Cost</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {models.map((m) => (
                                    <tr key={`${m.model_name}-${m.provider}`} className="hover:bg-gray-50">
                                        <td className="px-4 py-1.5">
                                            <span className="font-mono text-xs">{m.model_name}</span>
                                            <span className="text-[10px] text-gray-400 ml-1.5">{m.provider}</span>
                                        </td>
                                        <td className="px-4 py-1.5 text-right text-xs">{fmt(m.total_requests, 0)}</td>
                                        <td className="px-4 py-1.5 text-right text-xs">{fmt(m.total_tokens, 0)}</td>
                                        <td className="px-4 py-1.5"><CostBar value={m.total_cost} max={maxModelCost} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {heatmap && <HourlyHeatmapChart heatmap={heatmap.heatmap} peakHour={heatmap.peak_hour} peakDay={heatmap.peak_day} />}

            {health && (
                <Card title="Tracking Health">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8">
                        <InfoRow label="Total Logs" value={fmt(health.total_logs, 0)} />
                        <InfoRow label="Last Hour" value={health.last_hour_logs} />
                        <InfoRow label="Last Log" value={fmtDateTime(health.last_log_at)} />
                    </div>
                </Card>
            )}
        </div>
    );
}

function ByAgentTab({ dateRange }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        adminApi.getTokenUsageByAgent(dateRange)
            .then((res) => setData(res.agents || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [dateRange]);

    if (loading) return <Spinner size="lg" text="Loading..." center />;

    const maxCost = Math.max(...data.map(a => a.total_cost), 0.0001);

    return (
        <div className="space-y-4">
            {data.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No data</p>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Agent</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Function</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Reqs</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Tokens</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Avg Tok/Req</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Avg Time</th>
                                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 w-40">Cost</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {data.map((a) => (
                                <tr key={`${a.agent_name}-${a.agent_function}`} className="hover:bg-gray-50">
                                    <td className="px-4 py-2.5 font-mono text-xs">{a.agent_name}</td>
                                    <td className="px-4 py-2.5 text-xs text-gray-600">{a.agent_function}</td>
                                    <td className="px-4 py-2.5 text-right text-xs">{fmt(a.total_requests, 0)}</td>
                                    <td className="px-4 py-2.5 text-right text-xs">{fmt(a.total_tokens, 0)}</td>
                                    <td className="px-4 py-2.5 text-right text-xs">{fmt(a.avg_tokens_per_request, 0)}</td>
                                    <td className="px-4 py-2.5 text-right text-xs text-gray-500">
                                        {a.avg_response_time_ms ? `${Math.round(a.avg_response_time_ms)}ms` : '-'}
                                    </td>
                                    <td className="px-4 py-2.5"><CostBar value={a.total_cost} max={maxCost} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function CacheTab({ dateRange }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        adminApi.getTokenUsageCacheStats(dateRange)
            .then(setData)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [dateRange]);

    if (loading) return <Spinner size="lg" text="Loading..." center />;
    if (!data) return <p className="text-sm text-gray-400 text-center py-8">No data</p>;

    const hitPct = (data.cache_hit_rate * 100).toFixed(1);
    const maxModelCache = Math.max(...data.by_model.map(m => m.cache_tokens), 1);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Cache Hit Rate" value={`${hitPct}%`} />
                <StatCard label="Cache Tokens" value={fmt(data.total_cache_tokens, 0)} sub={`of ${fmt(data.total_input_tokens, 0)} input`} />
                <StatCard label="Reasoning Tokens" value={fmt(data.total_reasoning_tokens, 0)} />
                <StatCard label="Est. Savings" value={fmtCost(data.estimated_savings)} sub={`Cache cost: ${fmtCost(data.total_cache_cost)}`} />
            </div>

            <div className="border border-gray-200 rounded-lg bg-white px-5 py-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Cache Rate</h3>
                <div className="h-6 bg-gray-100 rounded-full overflow-hidden flex">
                    <div
                        className="h-full bg-emerald-500 transition-all flex items-center justify-center"
                        style={{ width: `${hitPct}%` }}
                    >
                        {data.cache_hit_rate > 0.05 && <span className="text-[10px] text-white font-medium">{hitPct}%</span>}
                    </div>
                </div>
                <div className="flex justify-between mt-1">
                    <span className="text-[10px] text-gray-400">0%</span>
                    <span className="text-[10px] text-gray-400">100%</span>
                </div>
            </div>

            {data.by_model.length > 0 && (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <div className="px-4 py-3 border-b border-gray-100">
                        <h3 className="text-sm font-medium text-gray-900">Cache by Model</h3>
                    </div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Model</th>
                                <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Input</th>
                                <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Cache</th>
                                <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Hit Rate</th>
                                <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Cache Cost</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {data.by_model.map((m) => (
                                <tr key={`${m.model_name}-${m.provider}`} className="hover:bg-gray-50">
                                    <td className="px-4 py-2">
                                        <span className="font-mono text-xs">{m.model_name}</span>
                                        <span className="text-[10px] text-gray-400 ml-1.5">{m.provider}</span>
                                    </td>
                                    <td className="px-4 py-2 text-right text-xs">{fmt(m.input_tokens, 0)}</td>
                                    <td className="px-4 py-2 text-right text-xs">{fmt(m.cache_tokens, 0)}</td>
                                    <td className="px-4 py-2 text-right text-xs font-mono">
                                        {(m.cache_hit_rate * 100).toFixed(1)}%
                                    </td>
                                    <td className="px-4 py-2 text-right text-xs font-mono">{fmtCost(m.cache_cost)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function SessionStatsTab({ dateRange }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        adminApi.getTokenUsageSessionStats(dateRange)
            .then(setData)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [dateRange]);

    if (loading) return <Spinner size="lg" text="Loading..." center />;
    if (!data) return <p className="text-sm text-gray-400 text-center py-8">No data</p>;

    const distEntries = Object.entries(data.cost_distribution);
    const maxDist = Math.max(...distEntries.map(([, v]) => v), 1);
    const maxExpCost = Math.max(...data.top_expensive.map(s => s.total_cost), 0.0001);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Total Sessions" value={fmt(data.total_sessions, 0)} />
                <StatCard label="Avg Cost / Session" value={fmtCost(data.avg_cost_per_session)} />
                <StatCard label="Avg Tokens / Session" value={fmt(data.avg_tokens_per_session, 0)} />
                <StatCard label="Avg Reqs / Session" value={fmt(data.avg_requests_per_session, 0)} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <div className="px-4 py-3 border-b border-gray-100">
                        <h3 className="text-sm font-medium text-gray-900">Cost Distribution</h3>
                    </div>
                    <div className="px-4 py-3 space-y-2">
                        {distEntries.map(([label, count]) => (
                            <div key={label} className="flex items-center gap-3">
                                <span className="text-xs text-gray-500 w-24 shrink-0 font-mono">{label}</span>
                                <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gray-900 rounded-full transition-all"
                                        style={{ width: `${(count / maxDist) * 100}%` }}
                                    />
                                </div>
                                <span className="text-xs text-gray-700 w-10 text-right font-mono">{count}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <div className="px-4 py-3 border-b border-gray-100">
                        <h3 className="text-sm font-medium text-gray-900">Top Expensive Sessions</h3>
                    </div>
                    {data.top_expensive.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-6">No sessions</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Session</th>
                                    <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Reqs</th>
                                    <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Tokens</th>
                                    <th className="px-4 py-2 text-xs font-medium text-gray-500 w-28">Cost</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.top_expensive.map((s) => (
                                    <tr key={s.session_id} className="hover:bg-gray-50">
                                        <td className="px-4 py-1.5 max-w-[200px]">
                                            <div className="flex items-center gap-1.5">
                                                <Link to={`/admin/sessions?session_id=${encodeURIComponent(s.session_id)}`} className="text-blue-600 hover:text-blue-800 hover:underline text-xs truncate">
                                                    {s.session_name || s.session_id}
                                                </Link>
                                                {s.user_id && (
                                                    <Link to={`/admin/users/${encodeURIComponent(s.user_id)}`} className="shrink-0 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded transition-colors truncate max-w-[80px]">
                                                        {s.user_id}
                                                    </Link>
                                                )}
                                            </div>
                                            {s.session_name && <span className="text-[10px] font-mono text-gray-400 truncate block">{s.session_id}</span>}
                                        </td>
                                        <td className="px-4 py-1.5 text-right text-xs">{s.total_requests}</td>
                                        <td className="px-4 py-1.5 text-right text-xs">{fmt(s.total_tokens, 0)}</td>
                                        <td className="px-4 py-1.5"><CostBar value={s.total_cost} max={maxExpCost} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

function ResponseTimeTab({ dateRange }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        adminApi.getTokenUsageResponseTime(dateRange)
            .then(setData)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [dateRange]);

    if (loading) return <Spinner size="lg" text="Loading..." center />;
    if (!data) return <p className="text-sm text-gray-400 text-center py-8">No data</p>;

    const ov = data.overall;
    const fmtMs = (ms) => ms != null ? (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`) : '-';

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Total Measured" value={fmt(ov.total, 0)} />
                <StatCard label="P50" value={fmtMs(ov.p50)} />
                <StatCard label="P95" value={fmtMs(ov.p95)} />
                <StatCard label="P99" value={fmtMs(ov.p99)} />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Avg" value={fmtMs(ov.avg_ms)} />
                <StatCard label="Min" value={fmtMs(ov.min_ms)} />
                <StatCard label="Max" value={fmtMs(ov.max_ms)} />
                <StatCard label="Slow (>5s)" value={data.slow_requests} />
            </div>

            {data.by_model.length > 0 && (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <div className="px-4 py-3 border-b border-gray-100">
                        <h3 className="text-sm font-medium text-gray-900">Response Time by Model</h3>
                    </div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Model</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Count</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Avg</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">P50</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">P95</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">P99</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {data.by_model.map((m) => (
                                <tr key={`${m.model_name}-${m.provider}`} className="hover:bg-gray-50">
                                    <td className="px-4 py-2.5">
                                        <span className="font-mono text-xs">{m.model_name}</span>
                                        <span className="text-[10px] text-gray-400 ml-1.5">{m.provider}</span>
                                    </td>
                                    <td className="px-4 py-2.5 text-right text-xs">{fmt(m.total, 0)}</td>
                                    <td className="px-4 py-2.5 text-right text-xs font-mono">{fmtMs(m.avg_ms)}</td>
                                    <td className="px-4 py-2.5 text-right text-xs font-mono">{fmtMs(m.p50)}</td>
                                    <td className="px-4 py-2.5 text-right text-xs font-mono">{fmtMs(m.p95)}</td>
                                    <td className="px-4 py-2.5 text-right text-xs font-mono">{fmtMs(m.p99)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function ByDayTab({ dateRange }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        adminApi.getTokenUsageByDay(dateRange)
            .then((res) => setData(res.days || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [dateRange]);

    if (loading) return <Spinner size="lg" text="Loading..." center />;

    const maxCost = Math.max(...data.map(d => d.total_cost), 0.0001);

    return (
        <div className="space-y-4">
            {data.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No data for this period</p>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Date</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Requests</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Tokens</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Users</th>
                                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 w-48">Cost</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {data.map((row) => (
                                <tr key={row.date} className="hover:bg-gray-50">
                                    <td className="px-4 py-2.5 font-mono text-xs">{row.date}</td>
                                    <td className="px-4 py-2.5 text-right">{fmt(row.total_requests, 0)}</td>
                                    <td className="px-4 py-2.5 text-right">{fmt(row.total_tokens, 0)}</td>
                                    <td className="px-4 py-2.5 text-right">{row.active_users}</td>
                                    <td className="px-4 py-2.5"><CostBar value={row.total_cost} max={maxCost} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function UserDetailSummary({ user, dateRange }) {
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        adminApi.getTokenUsageByModel({ ...dateRange, user_id: user.user_id })
            .then((res) => setModels(res.models || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [dateRange, user.user_id]);

    const maxCost = Math.max(...models.map(m => m.total_cost), 0.0001);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-xs text-gray-500">Requests</p>
                    <p className="text-lg font-semibold text-gray-900">{fmt(user.total_requests, 0)}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-xs text-gray-500">Tokens</p>
                    <p className="text-lg font-semibold text-gray-900">{fmt(user.total_tokens, 0)}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-xs text-gray-500">Total Cost</p>
                    <p className="text-lg font-semibold text-gray-900">{fmtCost(user.total_cost)}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-xs text-gray-500">Last Active</p>
                    <p className="text-lg font-semibold text-gray-900">{fmtDateTime(user.last_active)}</p>
                </div>
            </div>

            <h3 className="text-sm font-medium text-gray-700">Models Used</h3>
            {loading ? <Spinner text="Loading models..." center /> : models.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No model data</p>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Model</th>
                                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Provider</th>
                                <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Requests</th>
                                <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Tokens</th>
                                <th className="px-4 py-2 text-xs font-medium text-gray-500 w-44">Cost</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {models.map((row) => (
                                <tr key={`${row.model_name}-${row.provider}`} className="hover:bg-gray-50">
                                    <td className="px-4 py-2 font-mono text-xs">{row.model_name}</td>
                                    <td className="px-4 py-2"><Badge variant="outline">{row.provider}</Badge></td>
                                    <td className="px-4 py-2 text-right">{fmt(row.total_requests, 0)}</td>
                                    <td className="px-4 py-2 text-right">{fmt(row.total_tokens, 0)}</td>
                                    <td className="px-4 py-2"><CostBar value={row.total_cost} max={maxCost} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function UserDetailByDay({ user, dateRange }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        adminApi.getTokenUsageByDay({ ...dateRange, user_id: user.user_id })
            .then((res) => setData(res.days || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [dateRange, user.user_id]);

    if (loading) return <Spinner text="Loading..." center />;

    const maxCost = Math.max(...data.map(d => d.total_cost), 0.0001);

    return data.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">No daily data for this period</p>
    ) : (
        <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Date</th>
                        <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Requests</th>
                        <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Tokens</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500 w-44">Cost</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {data.map((row) => (
                        <tr key={row.date} className="hover:bg-gray-50">
                            <td className="px-4 py-2 font-mono text-xs">{row.date}</td>
                            <td className="px-4 py-2 text-right">{fmt(row.total_requests, 0)}</td>
                            <td className="px-4 py-2 text-right">{fmt(row.total_tokens, 0)}</td>
                            <td className="px-4 py-2"><CostBar value={row.total_cost} max={maxCost} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function UserDetailLogs({ user, dateRange }) {
    const [logs, setLogs] = useState({ logs: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [offset, setOffset] = useState(0);
    const [modelFilter, setModelFilter] = useState('');
    const [agentFilter, setAgentFilter] = useState('');
    const [selectedLog, setSelectedLog] = useState(null);
    const limit = 30;

    const load = useCallback(() => {
        setLoading(true);
        const params = { ...dateRange, user_id: user.user_id, limit, offset };
        if (modelFilter) params.model_name = modelFilter;
        if (agentFilter) params.agent_name = agentFilter;
        adminApi.getTokenUsageLogs(params)
            .then(setLogs)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [dateRange, user.user_id, offset, modelFilter, agentFilter]);

    useEffect(() => { load(); }, [load]);

    const handleApplyFilter = () => { setOffset(0); load(); };

    const modelOptions = useMemo(() => {
        const set = new Set(logs.logs.map(l => l.model_name));
        return [...set].sort();
    }, [logs.logs]);

    const agentOptions = useMemo(() => {
        const set = new Set(logs.logs.map(l => l.agent_name).filter(Boolean));
        return [...set].sort();
    }, [logs.logs]);

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
                <input
                    type="text"
                    value={modelFilter}
                    onChange={(e) => setModelFilter(e.target.value)}
                    placeholder="Filter model..."
                    list="user-detail-models"
                    className="h-7 px-3 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 w-44"
                />
                <datalist id="user-detail-models">
                    {modelOptions.map(m => <option key={m} value={m} />)}
                </datalist>
                <input
                    type="text"
                    value={agentFilter}
                    onChange={(e) => setAgentFilter(e.target.value)}
                    placeholder="Filter agent..."
                    list="user-detail-agents"
                    className="h-7 px-3 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 w-44"
                />
                <datalist id="user-detail-agents">
                    {agentOptions.map(a => <option key={a} value={a} />)}
                </datalist>
                <Button size="xs" onClick={handleApplyFilter}>Filter</Button>
                {(modelFilter || agentFilter) && (
                    <Button variant="ghost" size="xs" onClick={() => { setModelFilter(''); setAgentFilter(''); setOffset(0); }}>Clear</Button>
                )}
                <span className="text-xs text-gray-400 ml-auto">{logs.total} logs</span>
            </div>

            {loading ? <Spinner text="Loading logs..." center /> : logs.logs.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No logs found</p>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Model</th>
                                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Agent</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">In</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Out</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Cost</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Time</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {logs.logs.map((log) => (
                                <tr
                                    key={log.id}
                                    className="hover:bg-gray-50 cursor-pointer"
                                    onClick={() => setSelectedLog(log)}
                                >
                                    <td className="px-3 py-1.5 font-mono text-xs">{log.model_name}</td>
                                    <td className="px-3 py-1.5 text-xs text-gray-600">{log.agent_name}</td>
                                    <td className="px-3 py-1.5 text-right text-xs">{fmt(log.input_tokens, 0)}</td>
                                    <td className="px-3 py-1.5 text-right text-xs">{fmt(log.output_tokens, 0)}</td>
                                    <td className="px-3 py-1.5 text-right text-xs font-mono">{fmtCost(log.total_cost)}</td>
                                    <td className="px-3 py-1.5 text-right text-xs text-gray-500">{fmtDateTime(log.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="px-4 py-2">
                        <Pagination total={logs.total} limit={limit} offset={offset} onChange={setOffset} />
                    </div>
                </div>
            )}

            {selectedLog && (
                <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
            )}
        </div>
    );
}

function UserDetailModal({ user, dateRange, onClose }) {
    const [tab, setTab] = useState('summary');

    const detailTabs = [
        { id: 'summary', label: 'Summary' },
        { id: 'by-day', label: 'By Day' },
        { id: 'logs', label: 'Logs' },
    ];

    return (
        <Modal open onClose={onClose} title={`User: ${user.user_id}`} size="xl">
            <Modal.Body>
                <Tabs tabs={detailTabs} active={tab} onChange={setTab} className="mb-4" />
                {tab === 'summary' && <UserDetailSummary user={user} dateRange={dateRange} />}
                {tab === 'by-day' && <UserDetailByDay user={user} dateRange={dateRange} />}
                {tab === 'logs' && <UserDetailLogs user={user} dateRange={dateRange} />}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="ghost" onClick={onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
}

function ByUserTab({ dateRange }) {
    const [data, setData] = useState({ users: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [offset, setOffset] = useState(0);
    const [selectedUser, setSelectedUser] = useState(null);
    const limit = 50;

    useEffect(() => { setOffset(0); }, [dateRange]);

    useEffect(() => {
        setLoading(true);
        adminApi.getTokenUsageByUser({ ...dateRange, limit, offset })
            .then(setData)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [dateRange, offset]);

    if (loading) return <Spinner size="lg" text="Loading..." center />;

    const maxCost = Math.max(...data.users.map(u => u.total_cost), 0.0001);

    return (
        <div className="space-y-4">
            {data.users.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No data</p>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">User ID</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Requests</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Tokens</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Last Active</th>
                                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 w-48">Cost</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {data.users.map((row) => (
                                <tr
                                    key={row.user_id}
                                    className="hover:bg-gray-50 cursor-pointer"
                                    onClick={() => setSelectedUser(row)}
                                >
                                    <td className="px-4 py-2.5 font-mono text-xs truncate max-w-[200px]">{row.user_id}</td>
                                    <td className="px-4 py-2.5 text-right">{fmt(row.total_requests, 0)}</td>
                                    <td className="px-4 py-2.5 text-right">{fmt(row.total_tokens, 0)}</td>
                                    <td className="px-4 py-2.5 text-right text-xs text-gray-500">{fmtDateTime(row.last_active)}</td>
                                    <td className="px-4 py-2.5"><CostBar value={row.total_cost} max={maxCost} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="px-4 py-2">
                        <Pagination total={data.total} limit={limit} offset={offset} onChange={setOffset} />
                    </div>
                </div>
            )}

            {selectedUser && (
                <UserDetailModal
                    user={selectedUser}
                    dateRange={dateRange}
                    onClose={() => setSelectedUser(null)}
                />
            )}
        </div>
    );
}

function ByModelTab({ dateRange }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        adminApi.getTokenUsageByModel(dateRange)
            .then((res) => setData(res.models || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [dateRange]);

    if (loading) return <Spinner size="lg" text="Loading..." center />;

    const maxCost = Math.max(...data.map(m => m.total_cost), 0.0001);

    return (
        <div className="space-y-4">
            {data.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No data</p>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Model</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Provider</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Requests</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Tokens</th>
                                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 w-48">Cost</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {data.map((row) => (
                                <tr key={`${row.model_name}-${row.provider}`} className="hover:bg-gray-50">
                                    <td className="px-4 py-2.5 font-mono text-xs">{row.model_name}</td>
                                    <td className="px-4 py-2.5"><Badge variant="outline">{row.provider}</Badge></td>
                                    <td className="px-4 py-2.5 text-right">{fmt(row.total_requests, 0)}</td>
                                    <td className="px-4 py-2.5 text-right">{fmt(row.total_tokens, 0)}</td>
                                    <td className="px-4 py-2.5"><CostBar value={row.total_cost} max={maxCost} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function LogDetailModal({ log, onClose }) {
    return (
        <Modal open onClose={onClose} title={`Log #${log.id}`} size="xl">
            <Modal.Body>
                <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                    <InfoRow label="Tracking ID" value={log.tracking_id} mono />
                    <InfoRow label="User ID" value={log.user_id} mono />
                    <InfoRow label="Session ID" value={log.session_id} mono />
                    <InfoRow label="Message ID" value={log.message_id || '-'} mono />
                    <InfoRow label="Agent" value={log.agent_name} />
                    <InfoRow label="Function" value={log.agent_function} />
                    <InfoRow label="Model" value={log.model_name} mono />
                    <InfoRow label="Provider" value={log.provider} />
                </div>
                <Card title="Tokens">
                    <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                        <InfoRow label="Input Tokens" value={fmt(log.input_tokens, 0)} />
                        <InfoRow label="Output Tokens" value={fmt(log.output_tokens, 0)} />
                        <InfoRow label="Total Tokens" value={fmt(log.total_tokens, 0)} />
                        <InfoRow label="Cache Tokens" value={fmt(log.cache_tokens, 0)} />
                        <InfoRow label="Reasoning Tokens" value={fmt(log.reasoning_tokens, 0)} />
                        <InfoRow label="Response Time" value={log.response_time_ms ? `${log.response_time_ms}ms` : '-'} />
                    </div>
                </Card>
                <Card title="Costs">
                    <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                        <InfoRow label="Input Cost" value={fmtCost(log.input_cost)} mono />
                        <InfoRow label="Output Cost" value={fmtCost(log.output_cost)} mono />
                        <InfoRow label="Cache Cost" value={fmtCost(log.cache_cost)} mono />
                        <InfoRow label="Total Cost" value={fmtCost(log.total_cost)} mono />
                        <InfoRow label="Pricing Tier" value={log.prompt_tokens_tier || '-'} />
                    </div>
                </Card>
                <InfoRow label="Created At" value={fmtDateTime(log.created_at)} />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="ghost" onClick={onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
}

function LogsTab({ dateRange }) {
    const [data, setData] = useState({ logs: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [offset, setOffset] = useState(0);
    const [filters, setFilters] = useState({ user_id: '', model_name: '' });
    const [selectedLog, setSelectedLog] = useState(null);
    const limit = 50;

    useEffect(() => { setOffset(0); }, [dateRange]);

    const load = useCallback(() => {
        setLoading(true);
        adminApi.getTokenUsageLogs({ ...dateRange, limit, offset, ...filters })
            .then(setData)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [dateRange, offset, filters]);

    useEffect(() => { load(); }, [load]);

    const handleFilter = () => { setOffset(0); load(); };

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
                <input
                    type="text"
                    value={filters.user_id}
                    onChange={(e) => setFilters(prev => ({ ...prev, user_id: e.target.value }))}
                    placeholder="Filter user_id..."
                    className="h-7 px-3 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 w-44"
                />
                <input
                    type="text"
                    value={filters.model_name}
                    onChange={(e) => setFilters(prev => ({ ...prev, model_name: e.target.value }))}
                    placeholder="Filter model..."
                    className="h-7 px-3 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 w-44"
                />
                <Button size="xs" onClick={handleFilter}>Filter</Button>
            </div>

            {loading ? <Spinner size="lg" text="Loading..." center /> : data.logs.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No logs found</p>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">ID</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">User</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Model</th>
                                <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500">In</th>
                                <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500">Out</th>
                                <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500">Cost</th>
                                <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500">Time</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {data.logs.map((log) => (
                                <tr
                                    key={log.id}
                                    className="hover:bg-gray-50 cursor-pointer"
                                    onClick={() => setSelectedLog(log)}
                                >
                                    <td className="px-3 py-2 text-xs text-gray-400">{log.id}</td>
                                    <td className="px-3 py-2 font-mono text-xs truncate max-w-[120px]">{log.user_id}</td>
                                    <td className="px-3 py-2 text-xs">{log.model_name}</td>
                                    <td className="px-3 py-2 text-right text-xs">{fmt(log.input_tokens, 0)}</td>
                                    <td className="px-3 py-2 text-right text-xs">{fmt(log.output_tokens, 0)}</td>
                                    <td className="px-3 py-2 text-right text-xs font-mono">{fmtCost(log.total_cost)}</td>
                                    <td className="px-3 py-2 text-right text-xs text-gray-500">{fmtDateTime(log.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="px-4 py-2">
                        <Pagination total={data.total} limit={limit} offset={offset} onChange={setOffset} />
                    </div>
                </div>
            )}

            {selectedLog && (
                <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
            )}
        </div>
    );
}

const VALID_TABS = new Set([
    'overview', 'by-day', 'by-user', 'by-model', 'by-agent',
    'cache', 'sessions', 'response-time', 'logs',
]);

const VALID_PERIODS = new Set(['today', 'yesterday', '7', '14', '30', '90', 'custom']);

export default function AdminTokenUsage() {
    const [searchParams, setSearchParams] = useSearchParams();

    const activeTab = VALID_TABS.has(searchParams.get('tab')) ? searchParams.get('tab') : 'overview';
    const preset = VALID_PERIODS.has(searchParams.get('period')) ? searchParams.get('period') : 'today';
    const customStart = searchParams.get('from') || toISODate(new Date());
    const customEnd = searchParams.get('to') || toISODate(new Date());
    const [error, setError] = useState(null);

    const updateParam = useCallback((updates) => {
        setSearchParams((prev) => {
            Object.entries(updates).forEach(([k, v]) => {
                if (v != null) prev.set(k, v);
                else prev.delete(k);
            });
            return prev;
        }, { replace: true });
    }, [setSearchParams]);

    const setActiveTab = (tab) => updateParam({ tab });
    const setPreset = (p) => {
        const u = { period: p };
        if (p !== 'custom') { u.from = null; u.to = null; }
        updateParam(u);
    };
    const handleCustomChange = (start, end) => updateParam({ from: start, to: end });

    const dateRange = useMemo(
        () => getDateRange(preset, customStart, customEnd),
        [preset, customStart, customEnd],
    );

    const tabs = [
        { id: 'overview', label: 'Overview' },
        { id: 'by-day', label: 'By Day' },
        { id: 'by-user', label: 'By User' },
        { id: 'by-model', label: 'By Model' },
        { id: 'by-agent', label: 'By Agent' },
        { id: 'cache', label: 'Cache' },
        { id: 'sessions', label: 'Sessions' },
        { id: 'response-time', label: 'Response Time' },
        { id: 'logs', label: 'Logs' },
    ];

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Token Usage</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Monitor LLM token consumption, costs, and usage patterns</p>
                </div>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}

            <PeriodSelector
                preset={preset}
                onPresetChange={setPreset}
                customStart={customStart}
                customEnd={customEnd}
                onCustomChange={handleCustomChange}
            />

            <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} className="mt-4 mb-6" />

            {activeTab === 'overview' && <OverviewTab dateRange={dateRange} />}
            {activeTab === 'by-day' && <ByDayTab dateRange={dateRange} />}
            {activeTab === 'by-user' && <ByUserTab dateRange={dateRange} />}
            {activeTab === 'by-model' && <ByModelTab dateRange={dateRange} />}
            {activeTab === 'by-agent' && <ByAgentTab dateRange={dateRange} />}
            {activeTab === 'cache' && <CacheTab dateRange={dateRange} />}
            {activeTab === 'sessions' && <SessionStatsTab dateRange={dateRange} />}
            {activeTab === 'response-time' && <ResponseTimeTab dateRange={dateRange} />}
            {activeTab === 'logs' && <LogsTab dateRange={dateRange} />}
        </div>
    );
}
